import React, { useCallback } from 'react';
import { useIsNavigationLocked } from '@jira/polaris__component-navigation-store/src/useIsNavigationLocked.tsx';
import AddIcon from '@atlaskit/icon/core/add';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { CommonNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/common-nav-menu-button/main.tsx';
import { CreateViewMenu, type TriggerProps } from '../create-view-menu/CreateViewMenu.tsx';
import {
	useAnalyticsL2,
	useContainerId,
	useJpdNavCanManageViews,
	useJpdNavViewName,
} from '../../controllers/context.tsx';
import type { NavigationContainer } from '../../common/types.tsx';

const createView = defineMessage({
	defaultMessage: 'Create a view',
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.action-button-create-view.create-view',
	description: 'Label for the create view button in the views sidebar navigation menu.',
});

export type ActionButtonCreateViewProps = {
	navigationContainer: NavigationContainer;
	container?: string;
	sectionId?: string;
};

export const ActionButtonCreateViewOld = ({
	container,
	navigationContainer,
}: ActionButtonCreateViewProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const navViewName = useJpdNavViewName();
	const containerId = useContainerId();
	const [isNavigationLocked] = useIsNavigationLocked({ containerId });
	const canManageViews = useJpdNavCanManageViews();
	const isCreateButtonEnabled = canManageViews && !isNavigationLocked;
	const { sendCreateViewOnProjectMenuItemAnalyticsEvent } = useAnalyticsL2();

	const handleAnalyticsEvent = useCallback(() => {
		sendCreateViewOnProjectMenuItemAnalyticsEvent(navigationContainer);
	}, [navigationContainer, sendCreateViewOnProjectMenuItemAnalyticsEvent]);

	const renderTrigger = useCallback(
		({ onClick, ...rest }: TriggerProps) => {
			if (!isCreateButtonEnabled) {
				return null;
			}

			const createButton = (
				<CommonNavMenuButton
					{...rest}
					testId={`navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.action-button-create-view.button-${navViewName}`}
					icon={(iconProps) => <AddIcon {...iconProps} size="small" />}
					label={formatMessage(createView)}
					onClick={() => {
						onClick();
						handleAnalyticsEvent();
					}}
				/>
			);

			return <Tooltip content={formatMessage(createView)}>{createButton}</Tooltip>;
		},
		[formatMessage, handleAnalyticsEvent, isCreateButtonEnabled, navViewName],
	);

	return (
		<CreateViewMenu
			navigationContainer={navigationContainer}
			container={container}
			renderTrigger={renderTrigger}
		/>
	);
};
